/**
 * Keeping the tiles honest.
 *
 * The set a family starts with comes out of `FuelSetup`, and it is a guess —
 * five plausible names at five plausible prices. What a household actually
 * buys drifts within a month: the cinema becomes swimming, the ice cream costs
 * $6.50 now, and the "Takeaway" tile turns out to mean two different things.
 * Defaults nobody can correct stop matching reality, and the one-tap log stops
 * being worth a tap — which is the same failure as a stale gauge, one step
 * upstream of it.
 *
 * Nothing here is deleted. Archiving keeps the row, and the row is where the
 * history screen reads the name from, so retiring a tile costs a family
 * nothing they had already logged.
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useFamily } from '../lib/family.jsx';
import { BackLink } from '../ui/Nav.jsx';
import { addTile, archiveTile, updateTile, useFuel } from '../lib/fuel.js';
import { centsFromDollars, formatCents } from '../domain/money.js';
import { TILE_ICON_KEYS, TileIcon } from '../ui/Art.jsx';
import '../ui/QuickLog.css';

const dollarsOf = (cents) => (cents / 100).toFixed(2);

/**
 * What the form won't send.
 *
 * The column is `>= 0` and the label is 1-40 chars, so both of these are caught
 * either way — but a check constraint's message is written for whoever reads
 * the logs, not for a parent halfway through renaming a tile.
 */
function problemWith({ label, dollars }) {
  if (!label.trim()) return 'A tile needs a name — that is what the family reads on it.';
  if (centsFromDollars(dollars) < 0) return 'An amount can’t be negative. A tile logs money going out.';
  return null;
}

/**
 * Pick a drawing, or none.
 *
 * "No picture" is a real option and comes first, because a wrong picture is
 * worse than none — a bus beside "Ice cream" makes the tile *harder* to find
 * at a glance, which is the one job the picture has. Same argument as
 * `tileIconFor` refusing to guess; this is the parent's version of it.
 */
function IconPicker({ value, onChange, labelledBy }) {
  return (
    <div className="icon-picker" role="group" aria-labelledby={labelledBy}>
      <button
        type="button"
        className="small"
        aria-pressed={!value}
        onClick={() => onChange(null)}
      >
        No picture
      </button>
      {TILE_ICON_KEYS.map((key) => (
        <button
          type="button"
          key={key}
          aria-pressed={value === key}
          // The drawings have no names a family would recognise, so the
          // accessible name is the key. It is at least stable and describes
          // the thing drawn, which "Option 4" would not.
          aria-label={key.replace('-', ' ')}
          onClick={() => onChange(key)}
        >
          <TileIcon tile={{ icon: key }} />
        </button>
      ))}
    </div>
  );
}

/** The label/amount/icon form, shared by editing a tile and adding one. */
function TileFields({ label, dollars, icon, onChange, idPrefix }) {
  return (
    <>
      <div className="row">
        <label style={{ flex: '2 1 10rem' }}>
          Name
          <input
            value={label}
            maxLength={40}
            placeholder="Ice cream"
            onChange={(e) => onChange({ label: e.target.value })}
          />
        </label>
        <label style={{ flex: '1 1 7rem' }}>
          Usual amount
          <input
            type="number" inputMode="decimal" min="0" step="0.01"
            value={dollars}
            onChange={(e) => onChange({ dollars: e.target.value })}
          />
        </label>
      </div>
      <p className="small muted" id={`${idPrefix}-icon-label`}>Picture</p>
      <IconPicker
        value={icon}
        onChange={(next) => onChange({ icon: next })}
        labelledBy={`${idPrefix}-icon-label`}
      />
    </>
  );
}

export default function Tiles() {
  const { family } = useFamily();
  const fuel = useFuel(family?.id);
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState(null);
  // Two taps to archive, and the second one is a differently-labelled button
  // rather than a `window.confirm` — a native dialog can't be styled, can't be
  // read by the same screen the rest of this is on, and on a phone lands as a
  // system alert for what is a reversible tidy-up.
  const [confirming, setConfirming] = useState(null);
  const [adding, setAdding] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const tiles = fuel.tiles ?? [];

  /** Reports whether it worked, so a form that failed stays open with what was typed in it. */
  async function run(work) {
    setBusy(true);
    setError(null);
    let ok = true;
    try {
      await work();
      await fuel.reload();
    } catch (e) {
      setError(e.message);
      ok = false;
    }
    setBusy(false);
    return ok;
  }

  function startEdit(tile) {
    setConfirming(null);
    setEditing(tile.id);
    setDraft({ label: tile.label, dollars: dollarsOf(tile.defaultAmountCents), icon: tile.icon ?? null });
  }

  async function saveEdit(tile) {
    const problem = problemWith(draft);
    if (problem) { setError(problem); return; }
    const ok = await run(() => updateTile(tile.id, {
      label: draft.label.trim(),
      defaultAmountCents: centsFromDollars(draft.dollars),
      icon: draft.icon,
    }));
    if (!ok) return;
    setEditing(null);
    setDraft(null);
  }

  /**
   * Move one tile up or down.
   *
   * The whole list is renumbered from its new order rather than swapping two
   * `sort_order` values, because the values on disk are only guaranteed to be
   * *an* order — the setup flow wrote 0..4, but nothing stops two tiles sharing
   * a number, and swapping duplicates moves nothing. Only the rows that
   * actually changed are written.
   */
  async function move(index, delta) {
    const next = tiles.slice();
    const target = index + delta;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target], next[index]];
    await run(async () => {
      for (let i = 0; i < next.length; i += 1) {
        if (next[i].sortOrder !== i) await updateTile(next[i].id, { sortOrder: i });
      }
    });
  }

  async function add() {
    const problem = problemWith(adding);
    if (problem) { setError(problem); return; }
    const ok = await run(() => addTile({
      familyId: family.id,
      envelopeId: fuel.envelope.id,
      label: adding.label.trim(),
      defaultAmountCents: centsFromDollars(adding.dollars),
      icon: adding.icon,
      // Onto the end. A new tile joining the queue in the middle would move
      // every tile below it, and muscle memory is most of what makes the grid
      // fast to use.
      sortOrder: tiles.reduce((max, t) => Math.max(max, t.sortOrder ?? 0), -1) + 1,
    }));
    if (ok) setAdding(null);
  }

  return (
    <main className="app-shell stack">
      <div className="stack tight">
        <BackLink to="/">Home</BackLink>
        <h1>Quick tiles</h1>
      </div>

      <p className="muted">
        One tap on a tile logs its usual amount. Keep the list short — a tile you have to
        hunt for is slower than typing the number.
      </p>

      {error && <p className="notice error" role="alert">{error}</p>}

      {fuel.loading && <p className="muted" role="status">Loading…</p>}
      {fuel.error && <p className="notice error">{fuel.error}</p>}

      {!fuel.loading && !fuel.envelope && (
        <div className="card stack">
          <h2>No tank yet</h2>
          <p className="muted">Tiles log spends against the family tank, so that comes first.</p>
          <Link to="/fuel/setup"><button className="primary">Set up Family Fuel</button></Link>
        </div>
      )}

      {fuel.envelope && (
        <div className="card stack">
          {tiles.length === 0 && (
            <p className="muted">
              No tiles. Logging still works — “Something else” on the home screen takes an
              amount — but the one-tap part needs at least one.
            </p>
          )}

          <div className="tile-rows">
            {tiles.map((tile, i) => (
              editing === tile.id ? (
                <div className="stack tight" key={tile.id}>
                  <TileFields
                    label={draft.label}
                    dollars={draft.dollars}
                    icon={draft.icon}
                    idPrefix={`tile-${tile.id}`}
                    onChange={(patch) => setDraft((d) => ({ ...d, ...patch }))}
                  />
                  <div className="row">
                    <button className="primary" onClick={() => saveEdit(tile)} disabled={busy}>
                      {busy ? 'Saving…' : 'Save'}
                    </button>
                    <button className="quiet" onClick={() => { setEditing(null); setDraft(null); }}>
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div className="tile-row" key={tile.id}>
                  <TileIcon tile={tile} />
                  <div className="tile-row__name stack tight">
                    <span className="tile__label">{tile.label}</span>
                    <span className="tile__amount">{formatCents(tile.defaultAmountCents)}</span>
                  </div>
                  {confirming === tile.id ? (
                    <div className="tile-row__actions">
                      <button
                        className="primary"
                        disabled={busy}
                        onClick={async () => { await run(() => archiveTile(tile.id)); setConfirming(null); }}
                      >
                        Yes, archive it
                      </button>
                      <button className="quiet" onClick={() => setConfirming(null)}>Keep it</button>
                    </div>
                  ) : (
                    <div className="tile-row__actions">
                      <button className="quiet" onClick={() => startEdit(tile)} disabled={busy}>
                        Edit
                      </button>
                      <button
                        className="quiet"
                        onClick={() => move(i, -1)}
                        disabled={busy || i === 0}
                        aria-label={`Move ${tile.label} up`}
                      >
                        ↑
                      </button>
                      <button
                        className="quiet"
                        onClick={() => move(i, 1)}
                        disabled={busy || i === tiles.length - 1}
                        aria-label={`Move ${tile.label} down`}
                      >
                        ↓
                      </button>
                      <button className="quiet" onClick={() => { setEditing(null); setConfirming(tile.id); }} disabled={busy}>
                        Archive
                      </button>
                    </div>
                  )}
                </div>
              )
            ))}
          </div>

          <p className="small muted">
            Archiving takes a tile off the logging screen and leaves everything already
            logged through it alone — those spends keep its name in the history.
          </p>
        </div>
      )}

      {fuel.envelope && (
        adding ? (
          <div className="card stack">
            <h2>New tile</h2>
            <TileFields
              label={adding.label}
              dollars={adding.dollars}
              icon={adding.icon}
              idPrefix="new-tile"
              onChange={(patch) => setAdding((a) => ({ ...a, ...patch }))}
            />
            <div className="row">
              <button className="primary" onClick={add} disabled={busy}>
                {busy ? 'Adding…' : 'Add it'}
              </button>
              <button className="quiet" onClick={() => setAdding(null)}>Cancel</button>
            </div>
          </div>
        ) : (
          <button
            className="quiet"
            onClick={() => setAdding({ label: '', dollars: '5.00', icon: null })}
          >
            Add a tile
          </button>
        )
      )}
    </main>
  );
}
